import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  Check,
  Eye,
  Landmark,
  LucideIcon,
  MessageCircle,
  PlayCircle,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import PalommaLogoWithText from "@/assets/PalommaLogoWithText.svg";
import { ROUTES } from "@/config/routes";
import { track } from "@/helpers/posthog";
import { useMerchant } from "@/hooks/useMerchant";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { forgetTour } from "@/hooks/useTour";
import { ONBOARDING_LINKS } from "../../public/onboarding/constants";
import TenantPreviewModal from "./TenantPreviewModal";
import TutorialsSlideshowModal from "./TutorialsSlideshowModal";

type Visited = "inquilinos" | "conciliacion";

const StepCard = ({
  icon: Icon,
  tag,
  title,
  description,
  cta,
  primary,
  done,
  onClick,
}: {
  icon: LucideIcon;
  tag?: string;
  title: string;
  description: string;
  cta: string;
  primary?: boolean;
  done?: boolean;
  onClick: () => void;
}) => (
  <Card.Root
    bg="white"
    rounded="md"
    borderWidth={primary ? "2px" : "1px"}
    borderColor={primary ? "palomma.500" : "gray.200"}
    boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
  >
    <Card.Body p={5} display="flex" flexDirection="column" gap={3}>
      <HStack justify="space-between" align="flex-start">
        <Box
          bg={done ? "green.50" : "palomma.50"}
          color={done ? "green.600" : "palomma.600"}
          rounded="md"
          p={2}
        >
          {done ? <Check size={20} /> : <Icon size={20} />}
        </Box>
        {tag && (
          <Badge colorPalette="purple" rounded="full" px={2}>
            {tag}
          </Badge>
        )}
      </HStack>
      <Box flex="1">
        <Text fontWeight="semibold" fontFamily="heading" fontSize="md">
          {title}
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          {description}
        </Text>
      </Box>
      <Button
        size="sm"
        variant={primary ? "solid" : "outline"}
        onClick={onClick}
      >
        {cta}
      </Button>
    </Card.Body>
  </Card.Root>
);

const Chip = ({ label, done }: { label: string; done: boolean }) => (
  <HStack
    gap={1.5}
    bg="white"
    borderWidth="1px"
    borderColor={done ? "green.200" : "palomma.100"}
    rounded="full"
    px={2.5}
    py={1}
  >
    <Box
      w="14px"
      h="14px"
      rounded="full"
      bg={done ? "green.500" : "gray.200"}
      color="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {done && <Check size={10} />}
    </Box>
    <Text fontSize="xs" color={done ? "green.700" : "gray.600"}>
      {label}
    </Text>
  </HStack>
);

const OnboardingHome = (): JSX.Element => {
  const { merchant } = useMerchant();
  const { data: status } = useOnboardingStatus();
  const navigate = useNavigate();
  const [, setDismissed] = useLocalStorage(
    `palomma:onboarding-home-dismissed:${merchant?.id ?? ""}`,
    false
  );
  const [, setSeen] = useLocalStorage(
    `palomma:onboarding-home-seen:${merchant?.id ?? ""}`,
    false
  );
  const [visited, setVisited] = useLocalStorage<Visited[]>(
    `palomma:onboarding-visited:${merchant?.id ?? ""}`,
    []
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [videosOpen, setVideosOpen] = useState(false);

  useEffect(() => {
    if (!status) return;
    setSeen(true);
    track("onboarding_home_viewed", { invoice_count: status.invoiceCount });
  }, [status, setSeen]);

  if (!merchant) return <></>;

  const hasInvoice = (status?.invoiceCount ?? 0) >= 1;
  const visit = (step: Visited) => {
    if (!visited.includes(step)) setVisited([...visited, step]);
  };
  const done =
    Number(hasInvoice) +
    Number(visited.includes("inquilinos")) +
    Number(visited.includes("conciliacion"));

  const whatsappUrl = `${
    ONBOARDING_LINKS.whatsappSupport
  }?text=${encodeURIComponent(
    `Hola, soy de la inmobiliaria ${merchant.displayName} y necesito ayuda con mi onboarding`
  )}`;

  return (
    <Box minH="100vh" bg="white">
      <VStack
        maxW="5xl"
        mx="auto"
        px={{ base: 4, md: 12 }}
        py={{ base: 6, md: 10 }}
        align="stretch"
        gap={8}
      >
        <HStack justify="space-between">
          <Image src={PalommaLogoWithText} alt="Palomma" height="40px" />
          {merchant.logoUrl ? (
            <Image
              src={merchant.logoUrl}
              alt={merchant.displayName}
              height="40px"
              maxWidth="180px"
              objectFit="contain"
            />
          ) : (
            <Text fontSize="sm" color="gray.500">
              {merchant.displayName}
            </Text>
          )}
        </HStack>

        <Box>
          <Heading size="2xl" fontWeight="semibold" color="#1b074a">
            Tu portal está listo. ¿Qué quieres hacer hoy?
          </Heading>
          <Text color="gray.600" mt={2}>
            Tres pasos y tu operación queda andando. Empieza por el primero.
          </Text>
        </Box>

        <Card.Root
          bg="palomma.50"
          rounded="md"
          borderWidth="1px"
          borderColor="palomma.200"
          borderLeftWidth="4px"
          borderLeftColor="palomma.500"
          boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
          px={3}
          py={2}
        >
          <Card.Body p={0}>
            <HStack gap={3} flexWrap="wrap">
              <HStack gap={1.5}>
                <Text fontWeight="semibold" fontSize="sm" color="palomma.800">
                  Tu activación
                </Text>
                <Badge colorPalette="purple" rounded="full" px={1.5} size="sm">
                  {done} de 3
                </Badge>
              </HStack>
              <Chip label="Enlace de pagos listo" done />
              <Chip label="Primer recaudo cargado" done={hasInvoice} />
              <Chip
                label="Vista de inquilinos"
                done={visited.includes("inquilinos")}
              />
              <Chip
                label="Conciliación"
                done={visited.includes("conciliacion")}
              />
            </HStack>
          </Card.Body>
        </Card.Root>

        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <StepCard
            icon={Upload}
            tag="Lo primero"
            title={
              hasInvoice
                ? "Tus recaudos ya están aquí"
                : "Subir tu primer recaudo"
            }
            description={
              hasInvoice
                ? "Ya tienes cartera cargada. Mira cómo se gestiona y cómo se cobra cada recaudo."
                : "Con tu ERP, con un Excel o uno a uno. Tus inquilinos se crean solos."
            }
            cta={hasInvoice ? "Ver mis recaudos" : "Subir mis recaudos"}
            primary
            done={hasInvoice}
            onClick={() => {
              track("onboarding_step_clicked", { step: "recaudo" });
              forgetTour("recaudos");
              navigate(ROUTES.DASHBOARD.INVOICES);
            }}
          />
          <StepCard
            icon={Eye}
            title="Ver cómo lo ven tus inquilinos"
            description="Una vista del portal de pagos con tu logo, tal como les llega a ellos."
            cta="Ver el portal de pagos"
            done={visited.includes("inquilinos")}
            onClick={() => {
              track("onboarding_step_clicked", { step: "inquilinos" });
              visit("inquilinos");
              setPreviewOpen(true);
            }}
          />
          <StepCard
            icon={Landmark}
            title="Gestionar tu conciliación"
            description="Cada abono a tu cuenta con sus facturas y descuentos, listo para cruzar con el banco."
            cta="Ver cómo funciona"
            done={visited.includes("conciliacion")}
            onClick={() => {
              track("onboarding_step_clicked", { step: "conciliacion" });
              visit("conciliacion");
              forgetTour("conciliacion");
              navigate(ROUTES.DASHBOARD.SETTLEMENTS);
            }}
          />
        </SimpleGrid>

        <Card.Root
          bg="white"
          rounded="md"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
        >
          <Card.Body p={4}>
            <HStack justify="space-between" flexWrap="wrap" gap={3}>
              <Box>
                <Text fontWeight="semibold" fontSize="sm">
                  ¿Prefieres que te acompañemos?
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Mira los videos del portal o agenda 15 minutos con el equipo.
                </Text>
              </Box>
              <HStack gap={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    track("onboarding_step_clicked", { step: "video" });
                    setVideosOpen(true);
                  }}
                >
                  <PlayCircle size={16} />
                  Video
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      track("onboarding_step_clicked", { step: "whatsapp" })
                    }
                  >
                    <MessageCircle size={16} />
                    Agendar 15 min
                  </a>
                </Button>
              </HStack>
            </HStack>
          </Card.Body>
        </Card.Root>

        <Link
          as="button"
          alignSelf="center"
          fontSize="sm"
          color="gray.500"
          textDecoration="underline"
          textUnderlineOffset="4px"
          onClick={() => {
            track("onboarding_home_dismissed");
            setDismissed(true);
            navigate(ROUTES.DASHBOARD.HOME);
          }}
        >
          Ya conozco el portal, ir al resumen
        </Link>
      </VStack>

      <TenantPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      <TutorialsSlideshowModal
        open={videosOpen}
        onClose={() => setVideosOpen(false)}
      />
    </Box>
  );
};

export default OnboardingHome;
